import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '@/lib/axios';
import { getSessionId } from '@/lib/sessionId';
import { useFeatures } from '@/contexts/FeaturesContext';
import { useTranslation } from 'react-i18next';
import { 
  MapPin, 
  Calendar, 
  Globe, 
  Mail, 
  Phone,
  Clock,
  Star,
  Users,
  ArrowLeft,
  ExternalLink,
  Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Header } from '@/components/Header';
import { Footer } from '@/components/homepage/Footer';
import { typography, spacing, shadows, colors } from '@/components/business/design-system/businessDesignTokens';
import { Business } from '@/types/homepage';
import { ActivityForHomepage } from '@/types/homepage';
import { fetchPublicBusinessProfile, fetchPublicBusinessActivities, checkPublicCustomAdventuresAvailability, fetchPublicCustomAdventures } from '@/utils/publicBusinessApi';
import { WhatsAppIcon } from '@/components/shared/WhatsAppIcon';
import { PartnerTourCard } from '@/components/public/business/PartnerTourCard';
import { optimizedImage } from '@/lib/cloudinaryImage';

const PLACEHOLDER_VALUE = /^(not listed|not provided)$/i;
const hasValue = (value?: string) => !!value && !PLACEHOLDER_VALUE.test(value.trim());

const withWildyRef = (url: string) => {
  try {
    const target = new URL(url);
    target.searchParams.set('ref', 'wildy');
    return target.toString();
  } catch {
    return url;
  }
};

export function BusinessProfilePage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const features = useFeatures();
  const { t } = useTranslation('public', { keyPrefix: 'business.profile' });
  const { t: tc } = useTranslation('common');
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [activities, setActivities] = useState<ActivityForHomepage[]>([]);
  const [customAdventures, setCustomAdventures] = useState<any[]>([]);
  const [hasCustomAdventures, setHasCustomAdventures] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');

  // Handle tab selection from URL hash
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the # character
    if (hash && ['about', 'activities', 'custom'].includes(hash)) {
      setActiveTab(hash);
    }
  }, []);

  useEffect(() => {
    const loadBusinessData = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [businessData, activitiesData, customAdventuresData, customAdventuresList] = await Promise.all([
          fetchPublicBusinessProfile(businessId),
          fetchPublicBusinessActivities(businessId),
          checkPublicCustomAdventuresAvailability(businessId),
          fetchPublicCustomAdventures(businessId)
        ]);

        setBusiness(businessData);

        if (businessData?._id) {
          axiosInstance.post('/leads/track', {
            businessId: businessData._id,
            eventType: 'profile_view',
            source: 'direct',
            sessionId: getSessionId(),
          }).catch(() => {});
        }

        setActivities(activitiesData);
        setCustomAdventures(customAdventuresList);
        setHasCustomAdventures(customAdventuresData.hasCustomAdventures);
        
        // Debug: Log the activities data to see if images are included
        console.log('📸 Activities data with images:', activitiesData.map(activity => ({
          name: activity.name,
          hasImages: !!activity.images?.length,
          images: activity.images
        })));
        
        // Debug: Log the custom adventures data
        console.log('🎯 Custom Adventures data:', customAdventuresList.map(adventure => ({
          name: adventure.name,
          hasImages: !!adventure.images?.length,
          images: adventure.images
        })));
      } catch (error) {
        console.error('Error loading business data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBusinessData();
  }, [businessId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F0] flex items-center justify-center pt-[calc(4rem+env(safe-area-inset-top,0px))]">
        <Header />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#77A146] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading.message')}</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-[#F0F4F0] pt-[calc(4rem+env(safe-area-inset-top,0px))]">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
        <h2 className={cn(typography.h2, "text-slate-900")}>{t('notFound.title')}</h2>
        <p className={cn(typography.body, "text-slate-600 mt-2")}>{t('notFound.message')}</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          {tc('actions.backToHome')}
        </Button>
        </div>
      </div>
    );
  }

  const isRedirectBusiness = business.contact?.bookingMode === 'redirect';
  const showActivitiesTab = features.bookingPlatform && !isRedirectBusiness;
  const showTours = !showActivitiesTab && activities.length > 0;
  const showCustomTab = features.customAdventures && hasCustomAdventures;
  const partnerUrl = business.contact?.bookingUrl || business.contact?.website;
  const partnerHref = partnerUrl ? withWildyRef(partnerUrl) : undefined;
  const coverImage = Array.isArray(business.images?.cover) ? business.images?.cover[0] : business.images?.cover;
  const place = [business.location?.address?.city, business.location?.address?.country].filter(hasValue).join(', ');

  // Back to where the visitor came from on Wildy; straight to the map if they landed here from outside
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  const trackPartnerClick = () => {
    axiosInstance.post('/leads/track', {
      businessId: business._id,
      eventType: 'booking_click',
      source: 'direct',
      sessionId: getSessionId(),
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-[#F0F4F0] pt-[calc(4rem+env(safe-area-inset-top,0px))]">
      <Header />

      {/* Business Header with Cover Image */}
      <div className="relative h-80">
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${optimizedImage(coverImage, 1920) || '/images/default-business.jpg'})`,
            backgroundColor: '#1F2937' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60"></div>

        {business.illustrativeImages && (
          <p className="absolute top-4 right-4 rounded-lg bg-black/60 px-3 py-1 text-xs text-white">
            {t('illustrativeImages')}
          </p>
        )}
        
        {/* Navigation Buttons */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <Button
            onClick={goBack}
            variant="ghost"
            className="border border-white/40 bg-black/25 text-white backdrop-blur-sm hover:bg-black/40 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {tc('actions.back')}
          </Button>
          {features.customAdventures && hasCustomAdventures && (
            <Button
              onClick={() => navigate(`/custom-adventures/${businessId}`)}
              variant="ghost"
            className="border border-white/40 bg-black/25 text-white backdrop-blur-sm hover:bg-black/40 hover:text-white"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {t('customAdventures.title')}
            </Button>
          )}
        </div>

        {/* Business Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="container mx-auto">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-20 h-20 flex-none rounded-full bg-white shadow-lg overflow-hidden">
                <img 
                  src={optimizedImage(business.images?.logo, 240) || '/images/default-business.jpg'}
                  alt={business.name}
                  className="w-full h-full object-contain p-1.5"
                />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200/50 shadow-sm">
                    {t('types.' + business.type)}
                  </Badge>
                  {/*<div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="text-sm font-medium text-blue-700">{business.statistics?.averageRating || '4.5'}</span>
                  </div>*/}
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">{business.name}</h1>
                <div className="flex items-center text-white/80">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>{place}</span>
                </div>
              </div>
            </div>
            {partnerHref && (
              <Button asChild className="flex-none bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-600 text-white">
                <a href={partnerHref} target="_blank" rel="noopener noreferrer" onClick={trackPartnerClick}>
                  {t('cta.visitWebsite')}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </a>
              </Button>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs 
          value={activeTab} 
          onValueChange={(value) => {
            setActiveTab(value);
            window.history.replaceState(null, '', `#${value}`);
          }} 
          className="space-y-8"
        >
          {(showActivitiesTab || showCustomTab) && (
            <TabsList className="bg-white/50 backdrop-blur-sm">
              <TabsTrigger value="about">{t('tabs.about')}</TabsTrigger>
              {showActivitiesTab && (
                <TabsTrigger value="activities">{t('tabs.activities')}</TabsTrigger>
              )}
              {showCustomTab && (
                <TabsTrigger value="custom">{t('tabs.customAdventures')}</TabsTrigger>
              )}
            </TabsList>
          )}

          {/* About Tab */}
          <TabsContent value="about" className="space-y-8">
            {/* Description Card */}
            <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
              <CardHeader>
                <CardTitle>{t('sections.about')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{business.description}</p>
              </CardContent>
            </Card>

            {/* Read-only tours: visitors continue on the operator's own site */}
            {showTours && (
              <section aria-labelledby="tours-heading" className="space-y-4">
                <div>
                  <h2 id="tours-heading" className={cn(typography.h3, 'text-slate-900')}>{t('tours.title')}</h2>
                  <p className="text-sm text-slate-600">{t('tours.subtitle', { name: business.name })}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {activities.map((activity) => (
                    <PartnerTourCard
                      key={activity._id}
                      activity={activity}
                      fallbackImage={business.images?.gallery?.[0] || coverImage}
                      href={activity.bookingUrl ? withWildyRef(activity.bookingUrl) : partnerHref}
                      onLinkClick={trackPartnerClick}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Contact & Details Card */}
            <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
              <CardHeader>
                <CardTitle>{t('sections.details')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {business.contact?.email && (
                    <a 
                      href={`mailto:${business.contact.email}`}
                      className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60 hover:bg-slate-100/80 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-900">{t('contact.email')}</p>
                        <p className="text-sm text-slate-600">{business.contact.email}</p>
                      </div>
                    </a>
                  )}

                  {hasValue(business.contact?.phone) && (
                    <a 
                      href={`tel:${business.contact.phone}`}
                      className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60 hover:bg-slate-100/80 transition-colors"
                    >
                      <Phone className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-slate-900">{t('contact.phone')}</p>
                        <p className="text-sm text-slate-600">{business.contact.phone}</p>
                      </div>
                    </a>
                  )}

                  {business.contact?.website && (
                    <a 
                      href={withWildyRef(business.contact.website)}
                      onClick={trackPartnerClick}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60 hover:bg-slate-100/80 transition-colors"
                    >
                      <Globe className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-slate-900">{t('contact.website')}</p>
                        <p className="text-sm text-slate-600">{business.contact.website}</p>
                      </div>
                    </a>
                  )}

                  {business.contact?.whatsappNumber && (
                    <a 
                      href={`https://wa.me/${business.contact.whatsappNumber.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60 hover:bg-slate-100/80 transition-colors"
                    >
                      <WhatsAppIcon className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-slate-900">{t('contact.whatsapp')}</p>
                        <p className="text-sm text-slate-600">{business.contact.whatsappNumber}</p>
                      </div>
                    </a>
                  )}
                </div>

                {/* Operating Hours */}
                {/*{business.operatingHours && business.operatingHours.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold mb-4">{t('sections.hours')}</h3>
                    <div className="grid gap-2">
                      {business.operatingHours.map((hours, index) => (
                        <div key={index} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                          <span className="text-slate-600">{t(`days.${hours.day}`)}</span>
                          <span className="text-slate-900">
                            {hours.isClosed ? t('hours.closed') : `${hours.open} - ${hours.close}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}*/}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity) => (
                <Card 
                  key={activity._id}
                  className={cn(shadows.level1.className, "border-slate-200/60 hover:shadow-lg transition-shadow cursor-pointer")}
                  onClick={() => navigate(`/activity/${activity._id}`)}
                >
                  <div className="aspect-[16/9] w-full bg-gray-200 overflow-hidden">
                    <img
                      src={activity.images?.[0] || business.images?.gallery?.[0] || (Array.isArray(business.images?.cover) ? business.images?.cover[0] : business.images?.cover) || business.images?.logo || '/images/default-activity.jpg'}
                      alt={activity.name}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-2">{activity.name}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">{activity.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline">{activity.activityType}</Badge>
                      <span className="text-sm text-slate-600">
                        {activity.price?.amount ? tc('pricing.fromPrice', { price: activity.price.amount }) : tc('pricing.free')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Custom Adventures Tab */}
          {hasCustomAdventures && (
            <TabsContent value="custom" className="space-y-8">
              {customAdventures.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {customAdventures.map((adventure) => (
                    <Card 
                      key={adventure._id}
                      className={cn(shadows.level1.className, "border-slate-200/60 hover:shadow-lg transition-shadow cursor-pointer")}
                      onClick={() => navigate(`/custom-adventures/${businessId}?adventure=${adventure._id}`)}
                    >
                      <div className="aspect-[16/9] w-full bg-gray-200 overflow-hidden">
                        <img
                          src={adventure.images?.[0] || business?.images?.gallery?.[0] || (Array.isArray(business?.images?.cover) ? business?.images?.cover[0] : business?.images?.cover) || business?.images?.logo || '/images/default-adventure.jpg'}
                          alt={adventure.name}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        />
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-blue-600" />
                          </div>
                          <h3 className="font-semibold text-lg">{adventure.name}</h3>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-4">{adventure.description}</p>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            Custom Adventure
                          </Badge>
                          <span className="text-sm text-slate-600">
                            From {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: adventure.currency || 'USD'
                            }).format(adventure.basePrice || 0)}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle>{t('customAdventures.title')}</CardTitle>
                        <CardDescription>{t('customAdventures.description')}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-slate-600">{t('customAdventures.explanation')}</p>
                    <Button 
                      onClick={() => navigate(`/custom-adventures/${businessId}`)}
                      className="w-full bg-primary hover:bg-[#1F4220] text-white"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      {t('customAdventures.cta')}
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
      <Footer />
    </div>
  );
} 